/* ==========================================================================
   Conde · Cookie Consent

   Drives Shopify's Consent Tracking API. Without this file the markup in
   sections/conde-cookie-consent.liquid is inert — hiding a banner is not
   consent. Visitor choices are recorded through setTrackingConsent(); page
   loads and native-banner/checkout changes read Shopify's existing decision.

   Category -> Shopify consent field:
     Performance -> analytics
     Functional  -> preferences
     Targeting   -> marketing + sale_of_data
   Strictly Necessary is not represented; it is always on by definition.

   PostHog is our own script and is NOT covered by Shopify's API, so it is
   gated separately via opt_in_capturing / opt_out_capturing.

   Global Privacy Control: the approved copy commits us to honouring GPC
   ("Where required, we recognize supported browser-based opt-out signals,
   including Global Privacy Control"). navigator.globalPrivacyControl === true
   therefore forces optional categories off and suppresses the banner.
   ========================================================================== */

(function () {
  'use strict';

  var STORAGE_KEY = 'conde_cookie_consent';
  var CATEGORIES = ['performance', 'functional', 'targeting'];

  var root = document.querySelector('[data-conde-cookie-consent]');
  if (!root) return;

  var banner = root.querySelector('[data-cc-banner]');
  var modal = root.querySelector('[data-cc-modal]');
  var scrim = root.querySelector('[data-cc-scrim]');
  var scroll = root.querySelector('[data-cc-scroll]');

  var expiryDays = parseInt(root.dataset.expiryDays, 10) || 365;
  var defaultChecked = root.dataset.optionalDefault !== 'unchecked';
  var lastFocused = null;
  var appliedConsent = denyAll();
  var postHogTimer;

  /* ── Storage ──────────────────────────────────────────────────────────── */

  function readStored() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.at !== 'number') return null;
      var ageDays = (Date.now() - parsed.at) / 86400000;
      if (ageDays < 0 || ageDays > expiryDays || !parsed.consent) return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function writeStored(consent) {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ at: Date.now(), consent: consent, v: 1 })
      );
    } catch (e) {
      /* Private mode / storage disabled — consent still goes to Shopify below,
         we just cannot suppress the banner on the next page load. */
    }
  }

  /* ── Signals ──────────────────────────────────────────────────────────── */

  function gpcEnabled() {
    return window.navigator && window.navigator.globalPrivacyControl === true;
  }

  function denyAll() {
    return { performance: false, functional: false, targeting: false };
  }

  function allowAll() {
    return { performance: true, functional: true, targeting: true };
  }

  function effectiveConsent(consent) {
    if (gpcEnabled()) return denyAll();
    var out = {};
    CATEGORIES.forEach(function (cat) { out[cat] = !!consent && consent[cat] === true; });
    return out;
  }

  /* ── Applying consent ─────────────────────────────────────────────────── */

  function syncFromShopify() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.currentVisitorConsent !== 'function') return;
    var native = privacy.currentVisitorConsent();
    if (!native || !['analytics', 'preferences', 'marketing', 'sale_of_data'].some(function (key) {
      return native[key] === 'yes' || native[key] === 'no';
    })) return;
    // Read explicit choices, never regional defaults as if the visitor opted in.
    var next = effectiveConsent({
      performance: native.analytics === 'yes',
      functional: native.preferences === 'yes',
      targeting: native.marketing === 'yes' && native.sale_of_data !== 'no'
    });
    if (CATEGORIES.some(function (key) { return next[key] !== appliedConsent[key]; }) || !readStored()) {
      writeStored(next);
      apply(next, false);
      setChecks(next);
    }
    applyToPostHog(next);
    document.dispatchEvent(new CustomEvent('conde:consent', { detail: next }));
    hideBanner();
  }

  document.addEventListener('visitorConsentCollected', syncFromShopify);

  function applyToShopify(consent, recordDecision) {
    function send() {
      consent = effectiveConsent(appliedConsent);
      // Only a new interaction in our panel records consent. On page load the
      // native choice is authoritative, including a withdrawal in checkout.
      if (!recordDecision) {
        syncFromShopify();
        return;
      }
      var payload = {
        analytics: consent.performance,
        preferences: consent.functional,
        marketing: consent.targeting,
        sale_of_data: consent.targeting
      };
      try {
        window.Shopify.customerPrivacy.setTrackingConsent(payload, syncFromShopify);
      } catch (e) {
        /* no-op: API unavailable, nothing to enforce through Shopify */
      }
    }

    if (window.Shopify && window.Shopify.customerPrivacy) {
      send();
      return;
    }

    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function (err) {
          if (!err) send();
        }
      );
    }
  }

  var pageviewCaptured = false;
  var lastPostHog;
  var lastPostHogConsent;
  function applyToPostHog(consent) {
    var ph = window.posthog;
    if (!ph || !ph.__loaded) return;
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    var enabled = consent.performance === true && (!privacy ||
      typeof privacy.analyticsProcessingAllowed !== 'function' ||
      privacy.analyticsProcessingAllowed() === true);
    if (ph === lastPostHog && lastPostHogConsent === enabled) return;
    try {
      if (enabled) {
        if (typeof ph.has_opted_in_capturing !== 'function' || !ph.has_opted_in_capturing()) ph.opt_in_capturing();
      } else if (typeof ph.opt_out_capturing === 'function') {
        ph.opt_out_capturing();
      }
      if (enabled && !pageviewCaptured && typeof ph.capture === 'function') {
        ph.capture('$pageview');
        pageviewCaptured = true;
      }
      lastPostHog = ph;
      lastPostHogConsent = enabled;
      if (window.CondeTelemetry) window.CondeTelemetry.syncBridge(ph);
    } catch (e) { /* The loaded callback retries unavailable SDKs. */ }
  }
  document.addEventListener('conde:analytics-ready', function () {
    applyToPostHog(effectiveConsent(appliedConsent));
  });

  function apply(consent, recordDecision) {
    consent = effectiveConsent(consent);
    appliedConsent = consent;
    window.clearInterval(postHogTimer);
    applyToShopify(consent, recordDecision === true);
    applyToPostHog(effectiveConsent(appliedConsent));

    // posthog.js loads deferred and may not be ready on first call. Re-apply
    // once it is, so an opt-out is never silently dropped.
    if (!window.posthog || !window.posthog.__loaded) {
      var tries = 0;
      postHogTimer = window.setInterval(function () {
        tries += 1;
        if (window.posthog && window.posthog.__loaded) {
          applyToPostHog(effectiveConsent(appliedConsent));
          window.clearInterval(postHogTimer);
        } else if (tries > 40) {
          window.clearInterval(postHogTimer);
        }
      }, 250);
    }

    document.dispatchEvent(
      new CustomEvent('conde:consent', { detail: effectiveConsent(appliedConsent) })
    );
  }

  /* ── UI ───────────────────────────────────────────────────────────────── */

  function inputs() {
    return Array.prototype.slice.call(
      root.querySelectorAll('[data-cc-category]')
    );
  }

  function setChecks(consent) {
    consent = effectiveConsent(consent);
    inputs().forEach(function (el) {
      var cat = el.dataset.ccCategory;
      if (cat === 'necessary') {
        el.checked = true;
        return;
      }
      el.checked = !!consent[cat];
      el.disabled = gpcEnabled();
    });
  }

  function readChecks() {
    var out = {};
    CATEGORIES.forEach(function (cat) {
      var el = root.querySelector('[data-cc-category="' + cat + '"]');
      out[cat] = !!(el && el.checked);
    });
    return out;
  }

  function showBanner() {
    root.hidden = false;
    banner.hidden = false;
  }

  function hideBanner() {
    banner.hidden = true;
    if (modal.hidden) root.hidden = true;
  }

  function openModal() {
    setChecks(currentConsent());
    lastFocused = document.activeElement;
    root.hidden = false;
    scrim.hidden = false;
    modal.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    if (scroll) scroll.scrollTop = 0;
    var first = modal.querySelector('[data-cc-category="performance"]:not([disabled])') ||
      modal.querySelector('button:not([disabled]), [href]');
    if (first) first.focus();
    document.addEventListener('keydown', onKeydown);
  }

  function closeModal() {
    scrim.hidden = true;
    modal.hidden = true;
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', onKeydown);
    if (banner.hidden) root.hidden = true;
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  // Esc closes the modal but never counts as consent — if the banner was open
  // behind it, it stays open.
  function onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeModal();
      return;
    }
    if (e.key !== 'Tab') return;

    var focusables = modal.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    var list = Array.prototype.slice.call(focusables).filter(function (el) {
      return el.tabIndex >= 0 && !el.disabled && !el.closest('[hidden]') && el.getClientRects().length > 0;
    });
    if (!list.length) return;

    var first = list[0];
    var last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ── Commit paths ─────────────────────────────────────────────────────── */

  function commit(consent) {
    consent = effectiveConsent(consent);
    writeStored(consent);
    apply(consent, true);
    setChecks(consent);
    hideBanner();
    if (!modal.hidden) closeModal();
  }

  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-cc-accept-all]')) {
      commit(allowAll());
      return;
    }
    if (e.target.closest('[data-cc-save]')) {
      commit(readChecks());
      return;
    }
    if (e.target.closest('[data-cc-open]')) {
      openModal();
    }
  });

  // Deliberately no scrim click handler and no close button. The approved copy
  // dropped the "by continuing to navigate you consent" line, so dismissal
  // cannot be treated as acceptance and there is no way to dismiss without
  // choosing. Reopening later is handled by the footer link below.

  /* ── Public re-open hook ──────────────────────────────────────────────── */

  // Lets a footer link ("Cookie Settings" / "Do Not Sell or Share My Personal
  // Information") reopen the panel: <a href="#" data-conde-cookie-settings>
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-conde-cookie-settings]');
    if (!trigger) return;
    e.preventDefault();
    setChecks(currentConsent());
    openModal();
  });

  function currentConsent() {
    var stored = readStored();
    return effectiveConsent(stored ? stored.consent : (defaultChecked ? allowAll() : denyAll()));
  }

  window.condeCookieConsent = {
    open: openModal,
    get: currentConsent
  };

  /* ── Boot ─────────────────────────────────────────────────────────────── */

  var stored = readStored();

  if (gpcEnabled()) {
    setChecks(denyAll());
    // GPC is an explicit browser opt-out signal, not an automatic opt-in.
    apply(denyAll(), true);
    hideBanner();
    return;
  }

  if (stored && stored.consent) {
    // Returning visitor with a live choice — re-assert it, stay silent.
    setChecks(stored.consent);
    apply(stored.consent);
    return;
  }

  apply(denyAll());
  setChecks(defaultChecked ? allowAll() : denyAll());
  showBanner();
})();
